import React, { useState, useEffect } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { useOkapiKy, useStripes, IfPermission } from '@folio/stripes/core';
import { Loading, MessageBanner } from '@folio/stripes/components';
import SaveNewReportManager from '../components/reports/management/save/SaveNewReportManager';

/**
 * Route for editing report metadata only (name, desc, status, visibility, category, etc.)
 * Uses SaveNewReportForm in update mode — preserves queryMetadata and queryString unchanged.
 * Navigates back to the report view with the original list filters preserved.
 */
function EditMetadataRoute() {
  const { reportId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const okapiKy = useOkapiKy();
  const stripes = useStripes();
  const currentUserId = stripes.user?.user?.id;

  const [report, setReport] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const returnSearch = location.state?.returnSearch || '';

  useEffect(() => {
    okapiKy.get(`reports/${reportId}`).json()
      .then(data => {
        setReport(data);
        setIsLoading(false);
      })
      .catch(() => setIsLoading(false));
  }, [reportId]);

  // Redirect if user cannot edit
  useEffect(() => {
    if (!isLoading && report) {
      const permissions = report.permissions;
      if (!permissions) return;
      const owner = permissions.owner || '';
      if (!owner) return;
      if (currentUserId === owner) return;
      if (permissions.visibility === 'restricted' && (permissions.editors || []).includes(currentUserId)) return;
      history.replace({
        pathname: `/reports/reports-list/${reportId}/view`,
        search: returnSearch,
      });
    }
  }, [isLoading, report]);

  const handleClose = () => {
    history.push({
      pathname: `/reports/reports-list/${reportId}/view`,
      search: returnSearch,
    });
  };

  if (isLoading || !report) return <Loading size="xlarge" />;

  // Pass existing query so SaveNewReportManager preserves it unchanged
  const existingQuery = report.queryMetadata || report.queryString || {};

  return (
    <IfPermission perm="ui-reports.reports.edit">
      <SaveNewReportManager
      showQueryBuilderPane={false}
      setShowQueryBuilderPane={() => {}}
      showSavePane
      setShowSavePane={(show) => { if (!show) handleClose(); }}
      handleClose={handleClose}
      query={existingQuery}
      builderType={report.builderType || 'advanced'}
      builderState={report.builderState || {}}
      saveMode="update"
      savedReportId={reportId}
      savedReportName={report.name}
      savedReportDesc={report.desc}
    />
    </IfPermission>
  );
}

export default EditMetadataRoute;
